import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';

const toNum = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;

    return (
        d.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) +
        ' a las ' +
        new Date(value).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    );
};

const formatMoney = (value, currencyCode) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '—';

    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: currencyCode
    }).format(n);
};

const BadgeStatus = ({ status }) => {
    const s = Number(status);
    if (s === 1) return <span className="badge text-dark" style={{ backgroundColor: '#fd7e14' }}>Parcialmente recibida</span>;
    if (s === 2) return <span className="badge bg-success">Recibida</span>;
    if (s === 3) return <span className="badge bg-danger">Eliminada</span>;
    return <span className="badge bg-warning text-dark">Pendiente</span>;
};

const VerFacturaModal = ({ show, onHide, facturaId }) => {
    const [loading, setLoading] = useState(false);
    const [factura, setFactura] = useState(null);
    const [error, setError] = useState('');

    const [monedaVista, setMonedaVista] = useState('MXN');

    const cargarDetalles = async (id) => {
        setLoading(true);
        setError('');
        setFactura(null);

        try {
            const params = new URLSearchParams();
            params.append('id', id);

            const res = await axios.post(
                `${process.env.REACT_APP_API_ROUTE}/facturas/detalles`,
                params,
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
            );

            if (!res?.data?.success || !res?.data?.factura?._id) {
                throw new Error(res?.data?.msg || 'No se pudo cargar la factura');
            }

            setFactura(res.data.factura);
        } catch (e) {
            setError(e?.response?.data?.msg || e.message || 'Error al cargar la factura');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;
        if (!facturaId) return;
        cargarDetalles(facturaId);
    }, [show, facturaId]);

    const tc = useMemo(() => {
        const x = toNum(factura?.tipo_cambio);
        return x > 0 ? x : 1;
    }, [factura?.tipo_cambio]);

    const tieneConversion = useMemo(() => {
        return Number(factura?.moneda) === 1 && tc > 0;
    }, [factura?.moneda, tc]);

    useEffect(() => {
        if (!factura?._id) return;
        if (!tieneConversion) {
            setMonedaVista('MXN');
            return;
        }
        setMonedaVista('MXN');
    }, [factura?._id, tieneConversion]);

    const productos = useMemo(() => {
        return Array.isArray(factura?.productos) ? factura.productos : [];
    }, [factura]);

    const proveedorNombre = factura?.proveedor?.nombre || 'Sin definir';

    const monedaCode = monedaVista === 'USD' ? 'USD' : 'MXN';

    const convertir = (mxnValue) => {
        const mxn = toNum(mxnValue);
        if (monedaVista === 'USD') return Math.round((mxn / tc) * 100) / 100;
        return mxn;
    };

    const montoMostrar = convertir(factura?.monto);
    const montoCalculadoMostrar = convertir(factura?.monto_calculado);

    return (
        <Modal show={show} onHide={onHide} centered size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="d-flex flex-column">
                        <span>Detalle de factura</span>
                        <small className="text-muted">
                            {factura?.numero ? `Factura #${factura.numero}` : 'Factura'}{' '}
                            {factura?.status != null ? (
                                <>
                                    &nbsp;•&nbsp;<BadgeStatus status={factura.status} />
                                </>
                            ) : null}
                        </small>
                    </div>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loading && (
                    <div className="text-center py-4">
                        <div className="spinner-border" role="status" />
                        <div className="mt-2">Cargando detalles…</div>
                    </div>
                )}

                {!loading && error && (
                    <div className="alert alert-danger mb-0">
                        {error}
                    </div>
                )}

                {!loading && !error && factura && (
                    <>
                        <div className="border rounded p-3 mb-3">
                            <div className="row">
                                <div className="col-12 col-md-5 mb-2">
                                    <div className="text-muted">Proveedor</div>
                                    <div className="fw-semibold">{proveedorNombre}</div>
                                </div>

                                <div className="col-6 col-md-4 mb-2">
                                    <div className="text-muted">Fecha</div>
                                    <div className="fw-semibold">{formatDate(factura?.fecha)}</div>
                                </div>

                                <div className="col-6 col-md-3 mb-2">
                                    <div className="text-muted">Monto</div>

                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <div className="fw-semibold">
                                            {formatMoney(montoMostrar, monedaCode)}
                                        </div>

                                        {tieneConversion && (
                                            <>
                                                <select
                                                    className="form-select form-select-sm"
                                                    style={{ width: 110 }}
                                                    value={monedaVista}
                                                    onChange={(e) => setMonedaVista(e.target.value)}
                                                >
                                                    <option value="MXN">MXN</option>
                                                    <option value="USD">USD</option>
                                                </select>

                                                <div className="text-muted small">
                                                    TC: {tc}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <h6 className="mb-2">Productos</h6>

                        <div className="table-responsive">
                            <table className="table table-sm table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th>Categoría</th>
                                        <th>Subcategoría</th>
                                        <th>Código INRA</th>
                                        <th>Código proveedor</th>
                                        <th className="text-end">Cantidad</th>
                                        <th className="text-end">Costo</th>
                                        <th className="text-end">Público</th>
                                        <th className="text-end">Mínimo</th>
                                        <th className="text-end">Liquidación</th>
                                        <th className="text-end">Impuestos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!productos.length && (
                                        <tr>
                                            <td colSpan="11" className="text-center">Sin productos</td>
                                        </tr>
                                    )}

                                    {productos.map((p) => {
                                        const cantidad = p?.cantidad_surtir;
                                        const unidadNombre = p?.unidad?.nombre || '';
                                        const cantidadTexto =
                                            cantidad == null ? '—' : `${cantidad}${unidadNombre ? ` ${unidadNombre}` : ''}`;

                                        return (
                                            <tr key={p?._id}>
                                                <td className="fw-semibold">{p?.nombre || '—'}</td>
                                                <td>{p?.categoria?.nombre || '—'}</td>
                                                <td>{p?.subcategoria?.nombre || '—'}</td>
                                                <td>{p?.codigo_tienda || '—'}</td>
                                                <td>{p?.codigo_proveedor || '—'}</td>
                                                <td className="text-end">{cantidadTexto}</td>
                                                <td className="text-end">{formatMoney(convertir(p?.precio_unitario), monedaCode)}</td>
                                                <td className="text-end">{formatMoney(convertir(p?.precio_publico), monedaCode)}</td>
                                                <td className="text-end">{formatMoney(convertir(p?.precio_minimo), monedaCode)}</td>
                                                <td className="text-end">{formatMoney(convertir(p?.precio_liquidacion), monedaCode)}</td>
                                                <td className="text-end">{p?.impuestos ?? 0}%</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex justify-content-end mt-2">
                            <div className="text-end">
                                <div className="text-muted">Monto calculado</div>
                                <div className="fs-5 fw-bold">{formatMoney(montoCalculadoMostrar, monedaCode)}</div>
                                {tieneConversion && (
                                    <div className="text-muted small">
                                        Vista: {monedaVista} • TC: {tc}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                    Cerrar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default VerFacturaModal;